import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Loader } from "@mantine/core";
import { useLocation, useNavigate } from "react-router-dom";
import swal from "sweetalert";
import {
  getSkaiTokenPackages,
  getSkaiTokenUsage,
  updateSkaiTokenPurchase,
} from "../../../../../services/skaiTokens";
import { showError } from "../../../../../utils";
import { useSkaiAddon } from "./useSkaiAddon";

/** Small icon chip shown on each package card / confirm summary. */
function SkaiPkgIcon({ monthly }) {
  return (
    <span className={`skai-pkg-ico${monthly ? " amber" : ""}`} aria-hidden="true">
      {monthly ? (
        <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="var(--app-ink-amber)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="var(--app-text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16.5 9.4 7.5 4.21" />
          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
          <path d="m3.3 7 8.7 5 8.7-5" />
          <path d="M12 22V12" />
        </svg>
      )}
    </span>
  );
}

/**
 * SkaiCreditsCard — SkAi token management embedded in Billing & plan.
 *
 * Shows current SkAi token usage and a single "Buy tokens" popup where the admin
 * picks one option — a one-time token pack or the unlimited monthly subscription —
 * then confirms and pays in the same popup (no second modal).
 *
 * Reuses the existing token service so the purchase path is identical to the
 * legacy SkaiCredits page (PUT /skai/tokens-purchase). planType is derived from
 * the selected package (custom → monthly-subscription, otherwise onetime-purchase).
 */
export default function SkaiCreditsCard({ profile, paymentMethod, nextBillingDate }) {
  const navigate = useNavigate();
  const location = useLocation();
  const cardRef = useRef(null);
  const bodyRef = useRef(null);
  // Height of the picker view, held so the confirm view keeps the same size.
  const [bodyMinH, setBodyMinH] = useState(null);

  const tokenPackages = useSelector((state) => state.skai?.tokenPackages);

  const userId = profile?.user?.id;

  // Shared SkAi add-on state + cancel (also used by the Apps page & add-on list).
  const { record: existingRecord, isUnlimited, onMonthly, pendingCancel, accessUntil, tokenRemaining, tokenUsed, loading, cancelLoading: skaiCancelLoading, cancel, cancelModal } = useSkaiAddon(profile);

  // Single purchase popup: pick one option (one-time pack OR unlimited), confirm, pay.
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  // Confirm step is an in-modal view (same popup), not a second modal.
  const [confirmView, setConfirmView] = useState(false);
  // Final "authorize this charge" gate before we actually call the API.
  const [payConfirm, setPayConfirm] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  // Deep-link intent — preselect a package once packages have loaded.
  const [preselectPlan, setPreselectPlan] = useState(null);

  useEffect(() => {
    getSkaiTokenPackages(true);
  }, []);

  // Aggregate balance across the free monthly drop + any banked one-time tokens.
  const remaining = tokenRemaining;
  const used = tokenUsed;
  const originalTotal = remaining + used;
  const percentUsed = originalTotal > 0 ? Math.round((used / originalTotal) * 100) : 0;

  // Every purchasable package in one list — unlimited (custom) first as the
  // featured option, then one-time packs by ascending token count.
  const allPackages = useMemo(() => {
    if (!Array.isArray(tokenPackages)) return [];
    return tokenPackages
      .filter((p) => ["standard", "basic", "custom"].includes(p.plan))
      .sort((a, b) => {
        const rank = (p) => (p.plan === "custom" ? 0 : 1);
        if (rank(a) !== rank(b)) return rank(a) - rank(b);
        return (Number(a.tokens) || 0) - (Number(b.tokens) || 0);
      });
  }, [tokenPackages]);

  // Package under review + the planType we'll send for it.
  const confirmPkg = allPackages.find((p) => p._id === selectedPackage);
  const selectedPlanType =
    confirmPkg?.plan === "custom" ? "monthly-subscription" : "onetime-purchase";

  const openModal = () => {
    // Guard: trial / Free plan cannot buy SkAi tokens.
    if (profile?.user?.isTrialActive) {
      swal({
        title: "You are currently on a free trial",
        text: "Please subscribe to a paid plan to access SkAi tokens.",
        icon: "info",
        buttons: ["Cancel", "Subscribe"],
      }).then((ok) => ok && navigate("/home/settings?selectedTab=upgrade"));
      return;
    }
    if (profile?.subscription?.packageId?.name === "Free") {
      showError("You are on the Free plan. Upgrade your plan to access SkAi tokens.");
      return;
    }
    setModalOpen(true);
    setSelectedPackage(null);
    setConfirmView(false);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedPackage(null);
    setConfirmView(false);
    setPayConfirm(false);
    setPreselectPlan(null);
  };

  // Deep-link: arriving from the Apps "Subscribe" button (?skai=subscribe) or a
  // "?skai=buy" link scrolls to this card and opens the popup. "subscribe"
  // preselects the unlimited option. The param is stripped afterwards.
  useEffect(() => {
    const skai = new URLSearchParams(location.search).get("skai");
    if (!skai) return;
    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    openModal();
    setPreselectPlan(skai === "subscribe" ? "monthly-subscription" : "onetime-purchase");
    navigate("/home/settings?tab=billing-plan", { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Remember the picker's height so the confirm view can hold the same size.
  useEffect(() => {
    if (modalOpen && !confirmView && bodyRef.current) {
      setBodyMinH(bodyRef.current.offsetHeight);
    }
  }, [modalOpen, confirmView, allPackages]);

  // Apply a pending deep-link preselection once packages are available.
  useEffect(() => {
    if (!modalOpen || !preselectPlan || selectedPackage || !allPackages.length) return;
    const match =
      preselectPlan === "monthly-subscription"
        ? allPackages.find((p) => p.plan === "custom")
        : allPackages.find((p) => p.plan !== "custom");
    if (match) setSelectedPackage(match._id);
    setPreselectPlan(null);
  }, [modalOpen, preselectPlan, selectedPackage, allPackages]);

  const handlePurchase = async () => {
    if (!selectedPackage) return;
    setPurchasing(true);
    try {
      const res = await updateSkaiTokenPurchase(
        existingRecord?._id,
        { plan: selectedPackage, planType: selectedPlanType },
        userId
      );
      if (res?.status === 200) {
        closeModal();
        await getSkaiTokenUsage(userId);
        swal({
          title: "Success",
          text: "Your SkAi tokens have been updated.",
          icon: "success",
          button: "OK",
        });
      }
    } catch (err) {
      showError(err?.response?.data?.message || "Payment failed");
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <div className="card" ref={cardRef}>
      <div
        className="card-section"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}
      >
        <div>
          <div className="card-title">SkAi tokens</div>
          <div className="card-subtitle">
            AI note assist &amp; transcription. Tokens are used as your staff draft notes with SkAi.
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, flexShrink: 0, alignItems: "center" }}>
          {/* Status (moved up to the title): unlimited badge or token balance. */}
          {!loading ? (
            isUnlimited ? (
              <span className="badge badge-success">Unlimited</span>
            ) : (
              <div className="skai-usage-used">
                {used.toLocaleString()} used · {originalTotal.toLocaleString()} purchased
              </div>
            )
          ) : null}
          {/* One entry point — the popup covers both one-time packs and unlimited. */}
          {!isUnlimited ? (
            <button className="btn primary" onClick={openModal}>
              Buy tokens
            </button>
          ) : null}
          {/* Cancel only the explicit monthly SkAi subscription (not plan-bundled unlimited). */}
          {onMonthly ? (
            <button className="btn danger-ghost" onClick={cancel} disabled={skaiCancelLoading}>
              {skaiCancelLoading ? <Loader size="xs" color="#B42318" /> : "Cancel subscription"}
            </button>
          ) : pendingCancel ? (
            // Cancellation scheduled — unlimited access runs until period end.
            <span style={{ fontSize: 13, color: "#B42318", whiteSpace: "nowrap" }}>
              Cancels {accessUntil ? `on ${new Date(accessUntil).toLocaleDateString()}` : "at period end"}
            </span>
          ) : null}
        </div>
      </div>

      {/* Usage */}
      <div className="card-section">
        {loading ? (
          <div style={{ display: "flex", alignItems: "center", gap: 10, color: "var(--muted)", fontSize: 13 }}>
            <Loader size="xs" color="#FFBA45" /> Loading usage…
          </div>
        ) : (
          <div className="skai-usage">
            <div className="skai-usage-head">
              <div>
                <span className="skai-usage-balance">
                  {isUnlimited
                    ? used.toLocaleString()
                    : remaining.toLocaleString()}
                </span>
                <span className="skai-usage-balance-label">
                  {isUnlimited
                    ? " tokens used this billing period"
                    : " tokens remaining"}
                </span>
              </div>
            </div>

            {/* Credit balance bar (pay-as-you-go only). */}
            {!isUnlimited ? (
              <div className="skai-bar">
                <div
                  className="skai-bar-fill"
                  style={{ width: `${Math.min(100, percentUsed)}%` }}
                />
              </div>
            ) : null}

            <div
              className="field-hint"
              style={{
                marginTop: 10,
                lineHeight: 1.5,
                ...(!isUnlimited && remaining <= 0 ? { color: "#B42318" } : {}),
              }}
            >
              {isUnlimited
                ? onMonthly
                  ? "Unlimited tokens via your monthly SkAi add-on, so staff work without spending a token balance. Cancel anytime to switch back to pay-as-you-go tokens."
                  : "Unlimited tokens are included with the Gold plan, so staff work without spending a token balance. On other plans you can add an unlimited monthly add-on or buy tokens as needed."
                : remaining <= 0
                ? "You're out of tokens. Buy tokens or go unlimited to keep using SkAi."
                : `Staff have used ${used.toLocaleString()} of ${originalTotal.toLocaleString()} purchased tokens this billing period. Buy more anytime, or go unlimited.`}
            </div>
          </div>
        )}
      </div>

      {/* Purchase popup — one page for both options, then an in-popup confirm view */}
      <div
        style={{ zIndex: 1000 }}
        className={`modal-overlay${modalOpen ? " open" : ""}`}
        onClick={(e) => {
          if (e.target === e.currentTarget) closeModal();
        }}
      >
        <div className="modal-frame" style={{ maxWidth: 620 }}>
          <div className="modal-head">
            <div className="modal-head-title">
              {confirmView ? "Confirm purchase" : "SkAi tokens"}
            </div>
            <button type="button" className="modal-close" aria-label="Close" onClick={closeModal}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--app-text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div
            ref={bodyRef}
            className="modal-body skai-purchase-body"
            style={{
              background: "var(--app-surface)",
              padding: "20px 22px",
              minHeight: confirmView && bodyMinH ? bodyMinH : undefined,
            }}
          >
            {confirmView ? (
              /* ── Confirm view (same popup, replaces the picker) ── */
              <>
                <div className="skai-confirm-hd">
                  <span className="skai-confirm-hd-ico" aria-hidden="true">
                    <svg viewBox="0 0 24 24" width="22" height="22" fill="var(--app-on-primary)">
                      <path d="M12 2.5l1.9 5.6 5.6 1.9-5.6 1.9L12 17.5l-1.9-5.6L4.5 10l5.6-1.9L12 2.5z" />
                      <path d="M19 3l.8 2.2L22 6l-2.2.8L19 9l-.8-2.2L16 6l2.2-.8L19 3z" />
                    </svg>
                  </span>
                  <div>
                    <div className="skai-confirm-hd-name">SkAi</div>
                    <div className="skai-confirm-hd-type">
                      {selectedPlanType === "onetime-purchase"
                        ? "One-time payment"
                        : "Monthly subscription"}
                    </div>
                  </div>
                </div>
                <div className="skai-confirm-card">
                  <div className="skai-confirm-k">
                    {selectedPlanType === "monthly-subscription" ? "Plan" : "Tokens"}
                  </div>
                  <div className="skai-confirm-amount">
                    {confirmPkg?.tokens === "unlimited"
                      ? "Unlimited"
                      : `${Number(confirmPkg?.tokens || 0).toLocaleString()} tokens`}
                  </div>
                  <div className="skai-confirm-divider" />
                  <div className="skai-confirm-k">Details</div>
                  <ul className="skai-pkg-feats">
                    <li>AI note drafting</li>
                    {confirmPkg?.revisions ? <li>SkAi revisions</li> : null}
                    {confirmPkg?.voiceRecording ? <li>Speech to text</li> : null}
                    {selectedPlanType === "monthly-subscription" ? <li>Unlimited tokens</li> : null}
                  </ul>
                </div>
                <div className="skai-confirm-bill">
                  <div className="skai-confirm-bill-row">
                    <span className="skai-confirm-k">Payment method</span>
                    {paymentMethod ? (
                      <span className="skai-confirm-method">
                        <span className={`card-brand ${paymentMethod.brand}`}>
                          {paymentMethod.brandLabel}
                        </span>
                        •••• {paymentMethod.tail}
                      </span>
                    ) : (
                      <span className="skai-confirm-method skai-confirm-method-none">
                        No card on file
                      </span>
                    )}
                  </div>
                  <div className="skai-confirm-bill-row">
                    <span className="skai-confirm-k">
                      {selectedPlanType === "monthly-subscription" ? "Amount" : "Amount due"}
                    </span>
                    <span className="skai-confirm-due-amount">
                      ${confirmPkg?.price || 0}
                      {selectedPlanType === "monthly-subscription" ? " / month" : ""}
                    </span>
                  </div>
                </div>
                {payConfirm ? (
                  <div className="skai-confirm-agree">
                    <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="var(--app-ink-amber)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    <span>
                      You're authorizing a charge of{" "}
                      <strong>
                        ${confirmPkg?.price || 0}
                        {selectedPlanType === "monthly-subscription" ? " / month" : ""}
                      </strong>{" "}
                      to{" "}
                      {paymentMethod
                        ? `your ${paymentMethod.brandLabel} ending in ${paymentMethod.tail}`
                        : "your payment method on file"}
                      . Do you want to proceed?
                    </span>
                  </div>
                ) : (
                  <div className="skai-confirm-note">
                    {selectedPlanType === "monthly-subscription"
                      ? `Unlimited tokens activate right away. The $${confirmPkg?.price || 0}/month add-on will be included in your Ogiso subscription and billed to the card above from your next billing cycle${
                          nextBillingDate && nextBillingDate !== "-" ? ` (${nextBillingDate})` : ""
                        }.`
                      : `Your tokens are added right away. The $${confirmPkg?.price || 0} charge will be included in your Ogiso subscription invoice, billed to the card above on your next billing cycle${
                          nextBillingDate && nextBillingDate !== "-" ? ` (${nextBillingDate})` : ""
                        }.`}
                  </div>
                )}
                <div className="skai-modal-foot">
                  {payConfirm ? (
                    <>
                      <button
                        className="btn"
                        onClick={() => setPayConfirm(false)}
                        disabled={purchasing}
                      >
                        Cancel
                      </button>
                      <button className="btn primary" onClick={handlePurchase} disabled={purchasing}>
                        {purchasing ? (
                          <Loader size="xs" color="#171717" />
                        ) : (
                          `Confirm & pay $${confirmPkg?.price || 0}`
                        )}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        className="btn"
                        onClick={() => {
                          setConfirmView(false);
                          setPayConfirm(false);
                        }}
                      >
                        Back
                      </button>
                      <button className="btn primary" onClick={() => setPayConfirm(true)}>
                        Pay
                      </button>
                    </>
                  )}
                </div>
              </>
            ) : (
              /* ── One selection page: one-time packs + unlimited together ── */
              <>
                <div className="skai-modal-intro">
                  Choose an option to continue. Go unlimited for a flat monthly rate, or buy a
                  one-time token pack that never expires.
                </div>
                <div className="skai-pkg-grid">
                  {allPackages.map((pkg) => {
                    const sel = selectedPackage === pkg._id;
                    const isUnlimitedPkg = pkg.tokens === "unlimited";
                    const monthly = pkg.plan === "custom";
                    return (
                      <button
                        key={pkg._id}
                        type="button"
                        className={`skai-pkg${sel ? " sel" : ""}${monthly ? " skai-pkg-featured" : ""}`}
                        onClick={() => setSelectedPackage(pkg._id)}
                      >
                        <span className="skai-pkg-check" aria-hidden="true">
                          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="var(--app-on-primary)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </span>
                        <div className="skai-pkg-info">
                          {monthly ? <span className="skai-pkg-badge">Best value</span> : null}
                          <SkaiPkgIcon monthly={monthly} />
                          <div className="skai-pkg-kind">{monthly ? "Monthly subscription" : "One-time pack"}</div>
                          <div className="skai-pkg-amount">
                            {isUnlimitedPkg ? "Unlimited" : Number(pkg.tokens).toLocaleString()}
                            {!isUnlimitedPkg ? <span className="skai-pkg-unit"> tokens</span> : null}
                          </div>
                          <div className="skai-pkg-price">
                            <strong>${pkg.price}</strong>
                            <span className="skai-pkg-per">{monthly ? " / month" : " one-time"}</span>
                          </div>
                        </div>
                        <ul className="skai-pkg-feats">
                          <li>AI note drafting</li>
                          {pkg.revisions ? <li>SkAi revisions</li> : null}
                          {pkg.voiceRecording ? <li>Speech to text</li> : null}
                          {monthly ? <li>Unlimited tokens</li> : null}
                        </ul>
                      </button>
                    );
                  })}
                  {allPackages.length === 0 ? (
                    <div className="skai-modal-empty">No packages available right now.</div>
                  ) : null}
                </div>
                <div className="skai-modal-foot">
                  <button className="btn" onClick={closeModal}>Cancel</button>
                  <button
                    className="btn primary"
                    disabled={!selectedPackage}
                    onClick={() => setConfirmView(true)}
                  >
                    Continue
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {cancelModal}
    </div>
  );
}
